import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Lock, User, Eye, EyeOff, Loader2, ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthModal({ isOpen, onClose, onSuccess, onGoogleLogin, initialMode = "login", resetToken = null }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode); // "login", "register", "forgot", "reset", "add-password"
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [forgotEmailSent, setForgotEmailSent] = useState(false);
  const [resetTokenValid, setResetTokenValid] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [googleAccountEmail, setGoogleAccountEmail] = useState(""); // For add-password flow

  // Handle reset token from URL
  useEffect(() => {
    if (resetToken && isOpen) {
      setMode("reset");
      verifyResetToken(resetToken);
    }
  }, [resetToken, isOpen]);

  const verifyResetToken = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/verify-reset-token/${token}`);
      setResetTokenValid(response.data.valid);
      if (response.data.valid) {
        setMaskedEmail(response.data.email);
      } else {
        toast.error(response.data.reason || "Invalid reset link");
      }
    } catch (error) {
      setResetTokenValid(false);
      toast.error("Invalid or expired reset link");
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (mode === "forgot") {
      if (!formData.email) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Please enter a valid email";
      }
    } else if (mode === "reset" || mode === "add-password") {
      if (!formData.newPassword) {
        newErrors.newPassword = "New password is required";
      } else if (formData.newPassword.length < 6) {
        newErrors.newPassword = "Password must be at least 6 characters";
      }
      if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords don't match";
      }
    } else {
      if (!formData.email) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Please enter a valid email";
      }
      
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 6) {
        newErrors.password = "Password must be at least 6 characters";
      }
      
      if (mode === "register" && !formData.name.trim()) {
        newErrors.name = "Name is required";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      if (mode === "forgot") {
        await axios.post(`${API}/auth/forgot-password`, { email: formData.email });
        setForgotEmailSent(true);
        toast.success("Check your email for reset instructions");
      } else if (mode === "reset") {
        const response = await axios.post(`${API}/auth/reset-password`, {
          token: resetToken,
          new_password: formData.newPassword
        });
        
        // Store session token in localStorage as fallback for iOS
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        
        toast.success("Password reset successful!");
        onSuccess(response.data.user);
        onClose();
        
        // Clear URL params and navigate
        window.history.replaceState({}, document.title, window.location.pathname);
        
        if (response.data.user.has_completed_assessment) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/questionnaire', { replace: true });
        }
      } else if (mode === "add-password") {
        // Add password to Google account
        const response = await axios.post(`${API}/auth/add-password`, {
          email: googleAccountEmail,
          password: formData.newPassword
        });
        
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        
        toast.success("Password added! You can now login with email or Google.");
        onSuccess(response.data.user);
        onClose();
        
        if (response.data.user.has_completed_assessment) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/questionnaire', { replace: true });
        }
      } else {
        const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
        const payload = mode === "login" 
          ? { email: formData.email, password: formData.password }
          : { email: formData.email, password: formData.password, name: formData.name };
        
        const response = await axios.post(`${API}${endpoint}`, payload);
        
        // Store session token in localStorage as fallback for iOS
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        
        toast.success(mode === "login" ? "Welcome back!" : "Account created successfully!");
        onSuccess(response.data.user);
        onClose();
        
        // Reset form
        setFormData({ email: "", password: "", name: "", newPassword: "", confirmPassword: "" });
        setErrors({});
        
        if (response.data.user.has_completed_assessment) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/questionnaire', { replace: true });
        }
      }
    } catch (error) {
      console.error("Auth error:", error);
      
      let message = "Something went wrong. Please try again.";
      const statusCode = error.response?.status;
      
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      } else if (error.message === "Network Error") {
        message = "Network error. Please check your internet connection.";
      } else if (!error.response) {
        message = "Unable to connect to server. Please try again.";
      }
      
      // Handle Google account trying to login with password
      if (message.includes("Google sign-in")) {
        setGoogleAccountEmail(formData.email);
        setMode("add-password");
        setFormData({ ...formData, newPassword: "", confirmPassword: "" });
        toast.info("This account uses Google. Add a password to enable email login.", { duration: 5000 });
        setLoading(false);
        return;
      }
      
      // Handle rate limiting specifically
      if (statusCode === 429) {
        toast.error(message, { duration: 5000 });
        setErrors({ general: message });
      } else {
        toast.error(message);
        
        if (message.includes("already exists")) {
          setErrors({ general: message });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setErrors({});
    setForgotEmailSent(false);
  };

  const handleClose = () => {
    // Clear reset token from URL if present
    if (resetToken) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    setMode("login");
    setFormData({ email: "", password: "", name: "", newPassword: "", confirmPassword: "" });
    setErrors({});
    setForgotEmailSent(false);
    onClose();
  };

  if (!isOpen) return null;

  const renderContent = () => {
    // Forgot Password - Email Sent Success
    if (mode === "forgot" && forgotEmailSent) {
      return (
        <>
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="font-heading text-2xl text-cosmic-text-primary mb-2">
              Check Your Email
            </h2>
            <p className="text-cosmic-text-secondary text-sm">
              We've sent password reset instructions to<br />
              <span className="text-cosmic-text-primary font-medium">{formData.email}</span>
            </p>
          </div>

          <div className="space-y-4">
            <p className="text-cosmic-text-muted text-sm text-center">
              Didn't receive the email? Check your spam folder or
            </p>
            <Button
              type="button"
              onClick={() => setForgotEmailSent(false)}
              className="w-full py-3 btn-glass font-medium"
              data-testid="auth-resend-email"
            >
              Try Again
            </Button>
            <button
              type="button"
              onClick={() => switchMode("login")}
              className="w-full text-cosmic-text-secondary hover:text-cosmic-text-primary text-sm flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Sign In
            </button>
          </div>
        </>
      );
    }

    // Forgot Password Form
    if (mode === "forgot") {
      return (
        <>
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl text-cosmic-text-primary mb-2">
              Forgot Password?
            </h2>
            <p className="text-cosmic-text-secondary text-sm">
              Enter your email and we'll send you a reset link
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-cosmic-text-secondary mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-cosmic-text-muted" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                  className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-cosmic-text-primary placeholder:text-cosmic-text-muted focus:outline-none focus:border-cosmic-accent-gold/50 transition-colors"
                  data-testid="auth-forgot-email-input"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-3 btn-gold font-medium flex items-center justify-center gap-2"
              data-testid="auth-forgot-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Reset Link"
              )}
            </Button>
          </form>

          <button
            type="button"
            onClick={() => switchMode("login")}
            className="mt-6 w-full text-cosmic-text-secondary hover:text-cosmic-text-primary text-sm flex items-center justify-center gap-2"
            data-testid="auth-back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </button>
        </>
      );
    }

    // Reset Password Form
    if (mode === "reset") {
      if (resetTokenValid === null) {
        return (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-cosmic-accent-gold" />
            <p className="mt-4 text-cosmic-text-secondary">Verifying reset link...</p>
          </div>
        );
      }

      if (resetTokenValid === false) {
        return (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
              <X className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="font-heading text-2xl text-cosmic-text-primary mb-2">
              Link Expired
            </h2>
            <p className="text-cosmic-text-secondary text-sm mb-6">
              This password reset link has expired or is invalid.
            </p>
            <Button
              onClick={() => switchMode("forgot")}
              className="w-full py-3 btn-gold font-medium"
            >
              Request New Link
            </Button>
          </div>
        );
      }

      return (
        <>
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl text-cosmic-text-primary mb-2">
              Reset Password
            </h2>
            <p className="text-cosmic-text-secondary text-sm">
              Create a new password for<br />
              <span className="text-cosmic-text-primary font-medium">{maskedEmail}</span>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-cosmic-text-secondary mb-2">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-cosmic-text-muted" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.newPassword}
                  onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                  placeholder="At least 6 characters"
                  className="w-full pl-11 pr-12 py-3 bg-white/5 border border-white/10 rounded-xl text-cosmic-text-primary placeholder:text-cosmic-text-muted focus:outline-none focus:border-cosmic-accent-gold/50 transition-colors"
                  data-testid="auth-reset-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-cosmic-text-muted hover:text-cosmic-text-secondary transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-400">{errors.newPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-sm text-cosmic-text-secondary mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-cosmic-text-muted" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="Confirm your password"
                  className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-cosmic-text-primary placeholder:text-cosmic-text-muted focus:outline-none focus:border-cosmic-accent-gold/50 transition-colors"
                  data-testid="auth-reset-confirm-password"
                />
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-3 btn-gold font-medium flex items-center justify-center gap-2"
              data-testid="auth-reset-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Resetting...
                </>
              ) : (
                "Reset Password"
              )}
            </Button>
          </form>
        </>
      );
    }

    // Add Password to Google Account Form
    if (mode === "add-password") {
      return (
        <>
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl text-cosmic-text-primary mb-2">
              Add a Password
            </h2>
            <p className="text-cosmic-text-secondary text-sm">
              Your account <span className="text-cosmic-text-primary font-medium">{googleAccountEmail}</span> was created with Google.<br />
              Add a password to enable email login.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-cosmic-text-secondary mb-2">
                Create Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-cosmic-text-muted" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.newPassword}
                  onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                  placeholder="At least 6 characters"
                  className="w-full pl-11 pr-12 py-3 bg-white/5 border border-white/10 rounded-xl text-cosmic-text-primary placeholder:text-cosmic-text-muted focus:outline-none focus:border-cosmic-accent-gold/50 transition-colors"
                  data-testid="auth-add-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-cosmic-text-muted hover:text-cosmic-text-secondary transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-400">{errors.newPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-sm text-cosmic-text-secondary mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-cosmic-text-muted" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="Confirm your password"
                  className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-cosmic-text-primary placeholder:text-cosmic-text-muted focus:outline-none focus:border-cosmic-accent-gold/50 transition-colors"
                  data-testid="auth-add-password-confirm"
                />
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-400">{errors.confirmPassword}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-3 btn-gold font-medium flex items-center justify-center gap-2"
              data-testid="auth-add-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Adding Password...
                </>
              ) : (
                "Add Password & Sign In"
              )}
            </Button>
          </form>

          <button
            type="button"
            onClick={() => {
              setMode("login");
              setGoogleAccountEmail("");
            }}
            className="mt-6 w-full text-cosmic-text-secondary hover:text-cosmic-text-primary text-sm flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </button>
        </>
      );
    }

    // Login / Register Form
    return (
      <>
        <div className="text-center mb-8">
          <h2 className="font-heading text-2xl text-cosmic-text-primary mb-2">
            {mode === "login" ? "Welcome Back" : "Create Account"}
          </h2>
          <p className="text-cosmic-text-secondary text-sm">
            {mode === "login" 
              ? "Sign in to continue your journey" 
              : "Start your transformation today"}
          </p>
        </div>

        {/* Google Sign In Button */}
        <Button
          type="button"
          onClick={onGoogleLogin}
          className="w-full mb-6 py-3 bg-white hover:bg-gray-100 text-gray-800 font-medium flex items-center justify-center gap-3 rounded-xl transition-all"
          data-testid="auth-google-btn"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </Button>

        {/* Divider */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-cosmic-bg-secondary text-cosmic-text-muted">
              or continue with email
            </span>
          </div>
        </div>

        {/* Error Message */}
        {errors.general && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {errors.general}
          </div>
        )}

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Field (Register only) */}
          {mode === "register" && (
            <div>
              <label className="block text-sm text-cosmic-text-secondary mb-2">
                Your Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-cosmic-text-muted" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter your name"
                  className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-cosmic-text-primary placeholder:text-cosmic-text-muted focus:outline-none focus:border-cosmic-accent-gold/50 transition-colors"
                  data-testid="auth-name-input"
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-400">{errors.name}</p>
              )}
            </div>
          )}

          {/* Email Field */}
          <div>
            <label className="block text-sm text-cosmic-text-secondary mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-cosmic-text-muted" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-cosmic-text-primary placeholder:text-cosmic-text-muted focus:outline-none focus:border-cosmic-accent-gold/50 transition-colors"
                data-testid="auth-email-input"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-400">{errors.email}</p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm text-cosmic-text-secondary">
                Password
              </label>
              {mode === "login" && (
                <button
                  type="button"
                  onClick={() => switchMode("forgot")}
                  className="text-xs text-cosmic-accent-gold hover:underline"
                  data-testid="auth-forgot-password-link"
                >
                  Forgot password?
                </button>
              )}
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-cosmic-text-muted" />
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={mode === "register" ? "At least 6 characters" : "Enter your password"}
                className="w-full pl-11 pr-12 py-3 bg-white/5 border border-white/10 rounded-xl text-cosmic-text-primary placeholder:text-cosmic-text-muted focus:outline-none focus:border-cosmic-accent-gold/50 transition-colors"
                data-testid="auth-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-cosmic-text-muted hover:text-cosmic-text-secondary transition-colors"
                data-testid="auth-toggle-password"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-400">{errors.password}</p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full py-3 btn-gold font-medium flex items-center justify-center gap-2"
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {mode === "login" ? "Signing in..." : "Creating account..."}
              </>
            ) : (
              mode === "login" ? "Sign In" : "Create Account"
            )}
          </Button>
        </form>

        {/* Switch Mode */}
        <p className="mt-6 text-center text-cosmic-text-secondary text-sm">
          {mode === "login" ? "Don't have an account? " : "Already have an account? "}
          <button
            type="button"
            onClick={() => switchMode(mode === "login" ? "register" : "login")}
            className="text-cosmic-accent-gold hover:underline font-medium"
            data-testid="auth-switch-mode"
          >
            {mode === "login" ? "Sign up" : "Sign in"}
          </button>
        </p>
      </>
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-md glass-card p-8"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="auth-modal-close"
          >
            <X className="w-5 h-5 text-cosmic-text-secondary" />
          </button>

          {renderContent()}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
